import {
  addDoc,
  collection,
  doc,
  updateDoc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";

import { Collections } from "./constants";

/**
 * @typedef { import('./types').NoSQLOrganization } NoSQLOrganization
 */

/**
 * @param {import('firebase/firestore').Firestore} db The firestore database
 * @returns {object} The access functions for the organization
 */
export default function organizationsCollectionFns(db) {
  /**
   * @param {string} orgId The id of the organization to find
   * @returns {Promise<NoSQLOrganization|null>} An organization or nothing
   */
  async function getOrganization(orgId) {
    try {
      const docRef = doc(db, Collections.ORGANIZATIONS, orgId);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists() || docSnap.data().deletedAt) return null;
      return { id: docSnap.id, ...docSnap.data() };
    } catch (error) {
      console.error("Failed to get organization:", error);
      throw error;
    }
  }

  /**
   * @param {NoSQLOrganization} organization The organization to save
   * @returns {Promise<string>} Returns the organization ID
   */
  async function saveOrganization(organization) {
    const { id: orgId, ...orgData } = organization;

    try {
      if (orgId) {
        // Use updateDoc for existing orgs to preserve fields like deletedAt and createdAt
        const docRef = doc(db, Collections.ORGANIZATIONS, orgId);
        await updateDoc(docRef, orgData);
        return orgId;
      } else {
        const cleanedOrgData = {
          ...orgData,
          createdAt: serverTimestamp(),
          deletedAt: null,
        };
        const collectionRef = collection(db, Collections.ORGANIZATIONS);
        const docRef = await addDoc(collectionRef, cleanedOrgData);
        return docRef.id;
      }
    } catch (error) {
      console.error("Failed to save organization:", error);
      throw error;
    }
  }

  /**
   * @param {string} orgId The id of the organization to delete
   * @param {string} deletedBy the id of the user who deleted
   * @returns {Promise<void>} nothing
   */
  async function deleteOrganization(orgId, deletedBy) {
    try {
      const docRef = doc(db, Collections.ORGANIZATIONS, orgId);
      await updateDoc(docRef, {
        deletedAt: serverTimestamp(),
        deletedBy,
      });
    } catch (error) {
      console.error("Failed to delete organization:", error);
      throw error;
    }
  }

  return {
    getOrganization,
    saveOrganization,
    deleteOrganization,
  };
}
